import * as EVENTS from '../../common/constant';
import { pasteCleanupGroupingTags } from '../../common/config';
import { createElement, isNullOrUndefined as isNOU, detach, Browser, extend, getUniqueID } from '@syncfusion/ej2-base';
import { convertToBlob } from '../../common/util';
/**
 * PasteCleanup common action
 *
 * @hidden
 */
export class PasteCleanupAction {
    constructor(parent, pasteModel) {
        this.parent = parent;
        this.pasteModel = pasteModel;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on(EVENTS.INTERNAL_DESTROY, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off(EVENTS.INTERNAL_DESTROY, this.destroy);
    }
    /**
     * Updates the paste cleanup object with refreshed editor configuration and callback methods
     *
     * @param {IPasteModel} updatedPasteModel - The updated paste model with latest configuration
     * @returns {void} - This method does not return a value
     * @public
     * @hidden
     */
    updatePasteCleanupModel(updatedPasteModel) {
        this.pasteModel = updatedPasteModel;
    }
    /**
     * Extracts file extension from file mime type
     *
     * @param {string} fileName - The name of the file to extract extension from
     * @returns {string} The file extension (e.g., '.png', '.mp4')
     * @private
     */
    getExtensionFromMimeType(fileName) {
        const dotIndex = fileName.lastIndexOf('.');
        if (!fileName || dotIndex === -1) {
            return '';
        }
        const extension = fileName.substring(dotIndex).toLowerCase();
        return extension;
    }
    /**
     * Combines allowed types from all media settings (images, videos, audio)
     *
     * @returns {string[]} Array of allowed file extensions
     * @private
     */
    getCombinedAllowedExtensions() {
        // Array to hold combined allowed extensions
        const allowedExtensions = [];
        if (this.pasteModel) {
            // Add image allowed types
            if (this.pasteModel.insertImageSettings && this.pasteModel.insertImageSettings.allowedTypes) {
                allowedExtensions.push(...this.pasteModel.insertImageSettings.allowedTypes);
            }
            // Add video allowed types
            if (this.pasteModel.insertVideoSettings && this.pasteModel.insertVideoSettings.allowedTypes) {
                allowedExtensions.push(...this.pasteModel.insertVideoSettings.allowedTypes);
            }
            // Add audio allowed types
            if (this.pasteModel.insertAudioSettings && this.pasteModel.insertAudioSettings.allowedTypes) {
                allowedExtensions.push(...this.pasteModel.insertAudioSettings.allowedTypes);
            }
        }
        return allowedExtensions;
    }
    /**
     * Extracts files from clipboard data if available and filters by allowed extensions
     *
     * @param {NotifyArgs} e - The notification arguments containing clipboard data
     * @returns {File[] | null} Array of extracted files from clipboard or null if no valid files found
     * @public
     * @hidden
     */
    extractFileFromClipboard(e) {
        // Validate clipboard event and ensure clipboard items exist
        if (!e || !e.args || !e.args.clipboardData || e.args.clipboardData.items.length === 0) {
            // Return null if no clipboard data available
            return null;
        }
        // Get clipboard items from the paste event
        const clipboardItems = e.args.clipboardData.items;
        // Get combined allowed extensions from all media settings (images, videos, audio)
        const allowedExtensions = this.getCombinedAllowedExtensions();
        // Array to collect all valid files
        const pastedFiles = [];
        // Iterate through clipboard items to collect all valid files
        for (const item of Array.from(clipboardItems)) {
            // Skip non-file items
            if (item.kind !== 'file') {
                continue;
            }
            // Extract File object from clipboard item
            const file = item.getAsFile();
            if (file) {
                // Get the file extension from the mime type
                const fileExtension = this.getExtensionFromMimeType(file.name);
                // Only add file if extension is in allowed list
                if (allowedExtensions.indexOf(fileExtension) !== -1) {
                    pastedFiles.push(file);
                }
            }
        }
        // Return array of files if found, otherwise null
        return pastedFiles.length > 0 ? pastedFiles : null;
    }
    /**
     * Splits text by double line breaks and formats it according to editor's enter key configuration
     *
     * @param {string} value - The text value to be split and formatted
     * @returns {string} The formatted text with proper line breaks
     * @public
     * @hidden
     */
    splitBreakLine(value) {
        const enterSplitText = value.split(/\r\n\r\n|\n\n/g);
        let finalText = '';
        const startNode = this.getHtmlNode(true);
        const endNode = this.getHtmlNode(false);
        const isBrFormat = this.pasteModel.enterKey === 'BR';
        for (let i = 0; i < enterSplitText.length; i++) {
            const content = enterSplitText[i];
            const contentWithSpace = this.normalizeSpacesForHtml(content);
            const contentWithLineBreak = contentWithSpace.replace(/\r\n|\n/g, '<br>');
            if (i === 0) {
                if (isBrFormat && (i !== enterSplitText.length - 1 || contentWithLineBreak.endsWith('<br>'))) {
                    if (i !== enterSplitText.length - 1) {
                        finalText += (contentWithLineBreak + endNode + endNode);
                    }
                    else {
                        finalText += (contentWithLineBreak + endNode);
                    }
                }
                else {
                    finalText += contentWithLineBreak; // In order to merge the content in current line. No P/Div tag is added.
                }
            }
            else {
                if (isBrFormat) {
                    if (contentWithLineBreak.endsWith('<br>') || (contentWithLineBreak === '' && i === enterSplitText.length - 1)) {
                        finalText += (contentWithLineBreak + endNode);
                    }
                    else if (i === enterSplitText.length - 1) {
                        finalText += contentWithLineBreak;
                    }
                    else {
                        finalText += (contentWithLineBreak + endNode + endNode);
                    }
                }
                else {
                    if (contentWithLineBreak.trim() === '') {
                        finalText += '<br>';
                    }
                    finalText += startNode + contentWithLineBreak + endNode;
                }
            }
        }
        return finalText;
    }
    /**
     * Gets HTML node tag based on enterKey settings and whether it's start or end tag
     *
     * @param {boolean} isStartTag - Indicates whether to return start tag (true) or end tag (false)
     * @returns {string} The HTML node tag string
     * @public
     * @hidden
     */
    getHtmlNode(isStartTag) {
        if (this.pasteModel.enterKey === 'P') {
            return isStartTag ? '<p>' : '</p>';
        }
        else if (this.pasteModel.enterKey === 'DIV') {
            return isStartTag ? '<div>' : '</div>';
        }
        return isStartTag ? '' : '<br>';
    }
    /**
     * Converts spaces and tabs in text to HTML space entities.
     *
     * @param {string} text - The input text containing spaces and tabs to be converted
     * @returns {string} The text with spaces and tabs converted to HTML entities
     * @public
     * @hidden
     */
    normalizeSpacesForHtml(text) {
        let spacedContent = '';
        if (text === '') {
            return text;
        }
        const lineBreakSplitText = text.split(' ');
        for (let i = 0; i < lineBreakSplitText.length; i++) {
            const currentText = lineBreakSplitText[i];
            if (currentText === '') {
                spacedContent += '&nbsp;';
            }
            else if (currentText === '\t') {
                spacedContent += '&nbsp;&nbsp;&nbsp;&nbsp;';
            }
            else {
                if (i > 0 && i < lineBreakSplitText.length) {
                    spacedContent += ' ';
                }
                spacedContent += currentText;
            }
        }
        spacedContent = spacedContent.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
        spacedContent = spacedContent.replace(/&nbsp;&nbsp;/g, '&nbsp; ');
        return spacedContent;
    }
    /**
     * Converts base64 into file data.
     *
     * @param {string} base64 - The base64 encoded string to convert
     * @param {string} filename - The name for the resulting file
     * @returns {File} The converted file object
     * @public
     * @hidden
     */
    base64ToFile(base64, filename) {
        const baseStr = base64.split(',');
        const typeStr = baseStr[0].match(/:(.*?);/)[1];
        let extension = typeStr.split('/')[1];
        // Map quicktime MIME subtype to mov file extension
        if (extension === 'quicktime') {
            extension = 'mov';
        }
        const decodeStr = atob(baseStr[1]);
        let strLen = decodeStr.length;
        const decodeArr = new Uint8Array(strLen);
        while (strLen--) {
            decodeArr[strLen] = decodeStr.charCodeAt(strLen);
        }
        if (Browser.isIE || navigator.appVersion.indexOf('Edge') > -1) {
            const blob = new Blob([decodeArr], { type: extension });
            extend(blob, { name: filename + '.' + (!isNOU(extension) ? extension : '') });
            return blob;
        }
        else {
            return new File([decodeArr], filename + '.' + (!isNOU(extension) ? extension : ''), { type: extension });
        }
    }
    /**
     * Sets the image opacity to indicate upload in progress.
     *
     * @param {Element} imgElem - The image element to modify opacity for
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    setImageOpacity(imgElem) {
        imgElem.style.opacity = '0.5';
    }
    /**
     * Creates the popup element for upload progress.
     *
     * @returns {HTMLElement} The created popup element for displaying upload progress
     * @public
     * @hidden
     */
    createPopupElement() {
        const popupEle = createElement('div');
        this.pasteModel.rootContainer.appendChild(popupEle);
        return popupEle;
    }
    /**
     * Converts base64 media sources to blob URLs.
     *
     * @param {NodeListOf<HTMLImageElement | HTMLAudioElement | HTMLVideoElement>} mediaElement - Collection of media elements to process
     * @param {string} mediaType - The type of media being processed
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    getBlob(mediaElement, mediaType) {
        // Process each media element to convert base64 sources to blob URLs
        let sourceElement;
        for (let i = 0; i < mediaElement.length; i++) {
            // Determine the source element based on media type
            if (mediaType === 'audio' || mediaType === 'video') {
                sourceElement = mediaElement[i].querySelector('source');
            }
            else {
                sourceElement = mediaElement[i];
            }
            // Get the source URL from the element
            const sourceUrl = sourceElement.getAttribute('src');
            // Convert base64 source to blob URL if applicable
            if (!isNOU(sourceUrl) && sourceUrl.split(',')[0].indexOf('base64') >= 0) {
                const blobUrl = URL.createObjectURL(convertToBlob(sourceUrl));
                sourceElement.setAttribute('src', blobUrl);
            }
        }
    }
    /**
     * Removes Apple-specific line break elements from the HTML content.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to clean
     * @returns {HTMLElement} The cleaned HTML element with Apple-specific line breaks removed
     * @public
     * @hidden
     */
    cleanAppleClass(clipBoardElem) {
        const appleClassElem = clipBoardElem.querySelectorAll('br.Apple-interchange-newline');
        for (let i = 0; i < appleClassElem.length; i++) {
            detach(appleClassElem[i]);
        }
        return clipBoardElem;
    }
    /**
     * Removes denied tags and attributes as configured by paste cleanup settings.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to clean
     * @param {boolean} clean - Flag indicating whether cleanup should be performed
     * @returns {HTMLElement} The cleaned HTML element with denied tags and attributes removed
     * @public
     * @hidden
     */
    cleanupDeniedTagsAndAttributes(clipBoardElem, clean) {
        if (this.pasteModel.pasteCleanupSettings.deniedTags !== null) {
            clipBoardElem = this.deniedTags(clipBoardElem);
        }
        if (clean) {
            clipBoardElem = this.deniedAttributes(clipBoardElem, clean);
        }
        else if (this.pasteModel.pasteCleanupSettings.deniedAttrs !== null) {
            clipBoardElem = this.deniedAttributes(clipBoardElem, clean);
        }
        return clipBoardElem;
    }
    /**
     * Removes elements matching denied tags (with or without attribute selectors) from the provided clipboard element.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to process
     * @returns {HTMLElement} The cleaned HTML element with denied tags removed
     * @public
     * @hidden
     */
    deniedTags(clipBoardElem) {
        let deniedTags = isNOU(this.pasteModel.pasteCleanupSettings.deniedTags) ? [] :
            [...this.pasteModel.pasteCleanupSettings.deniedTags];
        deniedTags = this.attributesfilter(deniedTags);
        deniedTags = this.tagGrouping(deniedTags);
        for (let i = 0; i < deniedTags.length; i++) {
            const removableElement = clipBoardElem.querySelectorAll(deniedTags[i]);
            for (let j = removableElement.length - 1; j >= 0; j--) {
                const elementToRemove = removableElement[j];
                const parentElem = elementToRemove.parentNode;
                while (elementToRemove.firstChild) {
                    parentElem.insertBefore(elementToRemove.firstChild, elementToRemove);
                }
                parentElem.removeChild(elementToRemove);
            }
        }
        return clipBoardElem;
    }
    /**
     * Parses denied tags array and filters attributes, supporting allowed and denied (! prefix) attributes.
     *
     * @param {string[]} deniedTags - Array of denied tag strings to parse and filter
     * @returns {string[]} The filtered array of attribute strings
     * @public
     * @hidden
     */
    attributesfilter(deniedTags) {
        for (let i = 0; i < deniedTags.length; i++) {
            const currentDeniedTag = deniedTags[i];
            if (currentDeniedTag.split('[').length > 1) {
                const userAttributes = currentDeniedTag.split('[')[1].split(']')[0].split(',');
                const allowedAttributeArray = [];
                const deniedAttributeArray = [];
                for (let j = 0; j < userAttributes.length; j++) {
                    const currentUserAttrs = userAttributes[j];
                    if (userAttributes[j].indexOf('!') < 0) {
                        allowedAttributeArray.push(currentUserAttrs.trim());
                    }
                    else {
                        deniedAttributeArray.push(currentUserAttrs.split('!')[1].trim());
                    }
                }
                const allowedAttribute = allowedAttributeArray.length > 1 ?
                    (allowedAttributeArray.join('][')) : (allowedAttributeArray.join());
                const deniedAttribute = deniedAttributeArray.length > 1 ?
                    deniedAttributeArray.join('][') : (deniedAttributeArray.join());
                if (deniedAttribute.length > 0) {
                    const select = allowedAttribute !== '' ? currentDeniedTag.split('[')[0] +
                        '[' + allowedAttribute + ']' : currentDeniedTag.split('[')[0];
                    deniedTags[i] = select + ':not([' + deniedAttribute + '])';
                }
                else {
                    deniedTags[i] = currentDeniedTag.split('[')[0] + '[' + allowedAttribute + ']';
                }
            }
        }
        return deniedTags;
    }
    /**
     * Expands denied tag list by including related tags based on grouping definitions.
     *
     * @param {string[]} deniedTags - Array of denied tag strings to expand
     * @returns {string[]} The expanded array of denied tags including related tags
     * @public
     * @hidden
     */
    tagGrouping(deniedTags) {
        const groupingTags = [...deniedTags];
        const keys = Object.keys(pasteCleanupGroupingTags);
        const values = keys.map((key) => {
            return pasteCleanupGroupingTags[`${key}`];
        });
        const addTags = [];
        for (let i = 0; i < groupingTags.length; i++) {
            let currrentGroupTag = groupingTags[i];
            const groupIndex = keys.indexOf(currrentGroupTag);
            //The value split using '[' because to retrieve the tag name from the user given format which may contain tag with attributes
            if (currrentGroupTag.split('[').length > 1) {
                currrentGroupTag = currrentGroupTag.split('[')[0].trim();
            }
            if (keys.indexOf(groupingTags[i]) > -1) {
                for (let j = 0; j < values[groupIndex].length; j++) {
                    if (groupingTags.indexOf(values[groupIndex][j]) < 0 &&
                        addTags.indexOf(values[groupIndex][j]) < 0) {
                        addTags.push(values[groupIndex][j]);
                    }
                }
            }
        }
        return deniedTags = deniedTags.concat(addTags);
    }
    /**
     * Removes denied attributes from all elements in the provided clipboard element.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to process
     * @param {boolean} clean - Flag indicating whether cleanup should be performed
     * @returns {HTMLElement} The cleaned HTML element with denied attributes removed
     * @public
     * @hidden
     */
    deniedAttributes(clipBoardElem, clean) {
        const deniedAttrs = isNOU(this.pasteModel.pasteCleanupSettings.deniedAttrs) ? [] :
            [...this.pasteModel.pasteCleanupSettings.deniedAttrs];
        if (clean) {
            deniedAttrs.push('style');
        }
        for (let i = 0; i < deniedAttrs.length; i++) {
            const currentDeniedAttr = deniedAttrs[i];
            const removableAttrElement = clipBoardElem.
                querySelectorAll('[' + currentDeniedAttr + ']');
            for (let j = 0; j < removableAttrElement.length; j++) {
                removableAttrElement[j].removeAttribute(currentDeniedAttr);
            }
        }
        return clipBoardElem;
    }
    /**
     * Filters the inline 'style' attribute on all elements within the clipboard root element, leaving only allowed CSS style properties.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to process
     * @returns {HTMLElement} The processed HTML element with filtered style attributes
     * @public
     * @hidden
     */
    allowedStyle(clipBoardElem) {
        const allowedStyleProps = isNOU(this.pasteModel.pasteCleanupSettings.allowedStyleProps) ? [] :
            [...this.pasteModel.pasteCleanupSettings.allowedStyleProps];
        allowedStyleProps.push('list-style-type', 'list-style');
        const elementsWithStyle = clipBoardElem.querySelectorAll('[style]');
        for (let i = 0; i < elementsWithStyle.length; i++) {
            const currentStyleElem = elementsWithStyle[i];
            let allowedStyleValue = '';
            const allowedStyleValueArray = [];
            const styleValue = currentStyleElem.getAttribute('style').split(';');
            for (let k = 0; k < styleValue.length; k++) {
                if (allowedStyleProps.indexOf(styleValue[k].split(':')[0].trim()) >= 0) {
                    allowedStyleValueArray.push(styleValue[k]);
                }
            }
            currentStyleElem.removeAttribute('style');
            allowedStyleValue = allowedStyleValueArray.join(';').trim() === '' ?
                allowedStyleValueArray.join(';') : allowedStyleValueArray.join(';') + ';';
            if (allowedStyleValue) {
                currentStyleElem.setAttribute('style', allowedStyleValue);
            }
        }
        return clipBoardElem;
    }
    /**
     * Adds paste class to images and applies image properties.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content with images to process
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    setImageClassAndProps(clipBoardElem) {
        const allImg = clipBoardElem.getElementsByTagName('img');
        for (let i = 0, len = allImg.length; i < len; i++) {
            if (allImg[i].getAttribute('src') !== null) {
                allImg[i].className += ' pasteContent_Img';
            }
            this.setImageProperties(allImg[i]);
        }
        this.addTempClass(clipBoardElem);
    }
    /**
     * Sets width, height, and min/max styles for inserted images based on editor settings.
     *
     * @param {HTMLImageElement} allImg - The image element to apply properties to
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    setImageProperties(allImg) {
        if (this.pasteModel.insertImageSettings.width !== 'auto') {
            allImg.setAttribute('width', this.pasteModel.insertImageSettings.width);
        }
        if (this.pasteModel.insertImageSettings.minWidth !== '0' && this.pasteModel.insertImageSettings.minWidth !== 0) {
            allImg.style.minWidth = this.pasteModel.insertImageSettings.minWidth.toString();
        }
        if (this.pasteModel.insertImageSettings.maxWidth !== null) {
            allImg.style.maxWidth = this.pasteModel.getInsertImgMaxWidth().toString();
        }
        if (this.pasteModel.insertImageSettings.height !== 'auto') {
            allImg.setAttribute('height', this.pasteModel.insertImageSettings.height);
        }
        if (this.pasteModel.insertImageSettings.minHeight !== '0' && this.pasteModel.insertImageSettings.minHeight !== 0) {
            allImg.style.minHeight = this.pasteModel.insertImageSettings.minHeight.toString();
        }
        if (this.pasteModel.insertImageSettings.maxHeight !== null) {
            allImg.style.maxHeight = this.pasteModel.insertImageSettings.maxHeight.toString();
        }
    }
    /**
     * Temporarily adds a CSS class to all children of the clipboard element.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing clipboard content to add temporary classes to
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    addTempClass(clipBoardElem) {
        const allChild = clipBoardElem.children;
        for (let i = 0; i < allChild.length; i++) {
            allChild[i].classList.add('pasteContent_RTE');
        }
    }
    /**
     * Checks if there is any <picture> element present.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element to check for picture elements
     * @returns {boolean} True if picture element is found, false otherwise
     * @public
     * @hidden
     */
    hasPictureElement(clipBoardElem) {
        return clipBoardElem.getElementsByTagName('picture').length > 0;
    }
    /**
     * Processes all <picture> elements to resolve relative srcset attributes in <source> tags
     * using the base URI or the origin of the image source.
     *
     * @param {HTMLElement} clipBoardElem - The HTML element containing picture elements to process
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    processPictureElement(clipBoardElem) {
        const pictureElems = clipBoardElem.querySelectorAll('picture');
        const base = this.pasteModel.getDocument().baseURI;
        for (let i = 0; i < pictureElems.length; i++) {
            const imgElem = pictureElems[i].querySelector('img');
            const sourceElems = pictureElems[i].querySelectorAll('source');
            if (imgElem && imgElem.getAttribute('src')) {
                const srcValue = imgElem.getAttribute('src');
                const url = srcValue.indexOf('http') > -1 ? new URL(srcValue) : new URL(srcValue, base);
                for (let j = 0; j < sourceElems.length; j++) {
                    const srcset = sourceElems[j].getAttribute('srcset');
                    if (srcset) {
                        if (srcset.indexOf('http') === -1) {
                            const fullPath = url.origin + srcset;
                            sourceElems[j].setAttribute('srcset', fullPath);
                        }
                    }
                }
            }
        }
    }
    /**
     * Returns true if node has any content (text, images, or table).
     *
     * @param {HTMLElement} clipBoardElem - The HTML element to check for content
     * @returns {boolean} True if the element has content, false otherwise
     * @public
     * @hidden
     */
    hasContentToPaste(clipBoardElem) {
        const hasText = (clipBoardElem.textContent !== '') && (clipBoardElem.textContent.replace(/\u200B/g, '').trim() !== '');
        const hasImg = clipBoardElem.getElementsByTagName('img').length > 0;
        const hasTable = clipBoardElem.getElementsByTagName('table').length > 0;
        const hasBr = clipBoardElem.getElementsByTagName('br').length > 0;
        const hasVideo = clipBoardElem.getElementsByTagName('video').length > 0;
        const hasAudio = clipBoardElem.getElementsByTagName('audio').length > 0;
        return hasText || hasImg || hasTable || hasBr || hasVideo || hasAudio;
    }
    /**
     * Extracts base64-encoded images from the HTML content and converts them to File objects for upload.
     *
     * @param {HTMLElement} tempWrapperElem - The HTML element containing base64 images to extract
     * @returns {FileInfo[]} Array of FileInfo objects containing the converted file data
     * @public
     * @hidden
     */
    collectBase64ImageFiles(tempWrapperElem) {
        const filesData = [];
        if (!isNOU(tempWrapperElem.querySelector('img'))) {
            const imgElem = tempWrapperElem.querySelectorAll('img');
            const base64Src = [];
            const imgName = [];
            const uploadImg = [];
            for (let i = 0; i < imgElem.length; i++) {
                const src = imgElem[i].getAttribute('src');
                if (src && src.split(',')[0].indexOf('base64') >= 0) {
                    base64Src.push(src);
                    imgName.push(getUniqueID('rte_image'));
                    uploadImg.push(imgElem[i]);
                }
            }
            const fileList = [];
            let currentData;
            for (let i = 0; i < base64Src.length; i++) {
                fileList.push(this.base64ToFile(base64Src[i], imgName[i]));
                currentData = {
                    name: fileList[i].name,
                    rawFile: fileList[i],
                    size: fileList[i].size,
                    type: fileList[i].type,
                    status: '',
                    validationMessages: { minSize: '', maxSize: '' },
                    statusCode: '1'
                };
                filesData.push(currentData);
            }
        }
        return filesData;
    }
    /**
     * Adds appropriate class names to tables in the pasted content for formatting or standardization.
     *
     * @param {HTMLElement} element - The HTML element containing tables to add classes to
     * @param {string} [source] - Optional source parameter for context-specific formatting
     * @returns {HTMLElement} The processed HTML element with table classes added
     * @public
     * @hidden
     */
    addTableClass(element, source) {
        const tableElements = element.querySelectorAll('table');
        for (let i = 0; i < tableElements.length; i++) {
            const table = tableElements[i];
            const tableParentElement = table.parentElement;
            const isMSTeamsTable = tableParentElement &&
                (tableParentElement.nodeName === 'FIGURE');
            const hasCustomClass = table.classList.length > 0 &&
                table.classList.contains('e-rte-custom-table');
            if (hasCustomClass) {
                continue; // Skip the custom table class
            }
            if (this.pasteModel.pasteCleanupSettings.keepFormat && source && !isMSTeamsTable) {
                table.classList.add('e-rte-paste-' + source + '-table');
            }
            else if (!table.classList.contains('e-rte-table')) {
                table.classList.add('e-rte-table');
            }
            // Remove empty next sibling node (if any)
            const tableNextSibling = table.nextSibling;
            const shouldRemoveNextSibling = isNOU(table.nextElementSibling) &&
                tableNextSibling && tableNextSibling.textContent.trim() === '';
            if (shouldRemoveNextSibling) {
                detach(tableNextSibling);
            }
        }
        return element;
    }
    /**
     * Removes the temporary CSS class from elements and their class attribute if empty.
     *
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    removeTempClass() {
        const classElm = this.pasteModel.getEditPanel().querySelectorAll('.pasteContent_RTE');
        for (let i = 0; i < classElm.length; i++) {
            classElm[i].classList.remove('pasteContent_RTE');
            if (classElm[i].getAttribute('class') === '') {
                classElm[i].removeAttribute('class');
            }
        }
    }
    /**
     * Handles image cropping and blob-to-base64 conversion for images within the provided element.
     *
     * @param {HTMLElement} element - The HTML element containing images to be processed.
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    cropImageHandler(element) {
        const croppedImgs = element.querySelectorAll('.e-img-cropped');
        if (croppedImgs.length > 0) {
            this.processCroppedImages(croppedImgs);
        }
        else {
            this.handleBlobOrUpload();
        }
    }
    /**
     * Processes all images marked for cropping within the editor element using a for loop.
     *
     * @param {NodeListOf<HTMLImageElement>} croppedImgs - A NodeList of HTML image elements that are marked for cropping.
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    processCroppedImages(croppedImgs) {
        for (let i = 0; i < croppedImgs.length; i++) {
            const currentImage = croppedImgs[i];
            const src = currentImage.getAttribute('src');
            if (src && src.split(',')[0].indexOf('base64') >= 0) {
                const cropData = this.pasteModel.getCropImageData()[i];
                const tempImage = new Image();
                tempImage.src = src;
                tempImage.onload = () => {
                    const wRatio = cropData.goalWidth / tempImage.naturalWidth;
                    const hRatio = cropData.goalHeight / tempImage.naturalHeight;
                    const cropLeft = cropData.cropLength / wRatio;
                    const cropTop = cropData.cropTop / hRatio;
                    const cropWidth = (cropData.goalWidth - cropData.cropLength - cropData.cropR) / wRatio;
                    const cropHeight = (cropData.goalHeight - cropData.cropTop - cropData.cropB) / hRatio;
                    const canvas = document.createElement('canvas');
                    canvas.width = cropWidth;
                    canvas.height = cropHeight;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        // Draw the cropped portion of the image onto the canvas
                        ctx.drawImage(tempImage, cropLeft, cropTop, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
                        // Update the image source with the cropped image data
                        currentImage.setAttribute('src', canvas.toDataURL('image/png'));
                        currentImage.classList.remove('e-img-cropped');
                        this.pasteModel.imageUpload();
                        if (this.pasteModel.iframeSettings.enable) {
                            this.pasteModel.updateValue();
                        }
                    }
                };
            }
        }
    }
    /**
     * Handles blob image conversion to base64 (based on clipboard content) or the general image upload/updateValue logic.
     *
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    handleBlobOrUpload() {
        const inputElement = this.pasteModel.getEditPanel();
        const inputImgs = inputElement.querySelectorAll('img');
        const needsBlobConversion = inputImgs.length > 0 &&
            inputImgs[0].src.startsWith('blob') &&
            !isNOU(this.pasteModel.insertImageSettings.saveUrl) &&
            !isNOU(this.pasteModel.insertImageSettings.path);
        if (needsBlobConversion) {
            // Based on the information in your clipboard, convert blob src 'img' elements to base64 if needed
            this.convertBlobToBase64(inputElement);
            this.iframeUploadTime = setTimeout(() => {
                this.pasteModel.imageUpload();
                if (this.pasteModel.iframeSettings.enable) {
                    this.pasteModel.updateValue();
                }
            }, 20);
        }
        else {
            this.pasteModel.imageUpload();
            if (this.pasteModel.iframeSettings.enable && !this.pasteModel.enableXhtml) {
                this.pasteModel.updateValue();
            }
        }
    }
    /**
     * Converts all <img> elements with a blob URL source inside the provided element to base64.
     *
     * @param {HTMLElement} element - The HTML element containing image elements to be converted from blob URLs to base64.
     * @returns {void} Nothing is returned
     * @public
     * @hidden
     */
    convertBlobToBase64(element) {
        const imgElem = element.querySelectorAll('img');
        for (let i = 0; i < imgElem.length; i++) {
            const imgUrl = imgElem[i].getAttribute('src');
            if (imgUrl && imgUrl.startsWith('blob')) {
                const tempImage = new Image();
                // Once the blob image is loaded, draw it on a canvas and get the base64 string
                const onImageLoadEvent = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    canvas.width = tempImage.width;
                    canvas.height = tempImage.height;
                    ctx.drawImage(tempImage, 0, 0);
                    const base64String = canvas.toDataURL('image/png');
                    // Replace the <img> src with the base64
                    imgElem[i].src = base64String;
                    tempImage.removeEventListener('load', onImageLoadEvent);
                };
                tempImage.src = imgUrl;
                tempImage.addEventListener('load', onImageLoadEvent);
            }
        }
    }
    /**
     * Cleans up resources when the component is destroyed
     *
     * @returns {void} - No return value
     * @public
     */
    destroy() {
        if (this.iframeUploadTime) {
            clearTimeout(this.iframeUploadTime);
            this.iframeUploadTime = null;
        }
        this.removeEventListener();
    }
}
